import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useMemo, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step2 from "@/components/menu-management/course/add-course/step2";
import Step1 from "@/components/menu-management/course/add-course/step1";
import Step3 from "@/components/menu-management/course/add-course/step3";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewCourse,
  clearCourseMessages,
  updateExistingCourse,
} from "@/redux/slice/menu-manage-slice/courses-slice";
import { toast } from "react-hot-toast";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";
import { useSelector, useDispatch } from "react-redux";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { useNavigate, useParams, useLocation } from "react-router-dom";

export const course_id = 12;

function CreateCourse() {
  const navigate = useNavigate();
  const { type } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [dayTimes, setDayTimes] = useState([]);
  const item = location.state;

  const { createLoading, error, updateLoading } = useSelector(
    (state) => state.menuManage.course
  );
  const { getLoading } = useSelector((state) => state.menuManage.menu);

  const form = useForm({
    resolver: zodResolver(createCourseschema),
    mode: "onChange",
    defaultValues: {
      type: "create",
      item_no: "",
      name: "",
      description: "",
      in_price: null,
      out_price: null,
      course_items: [],
      vat: 0,
      status: "active",
      file_manager_id: undefined,
    },
  });

  const errorMessage = getFirstErrorMessage(form);

  useEffect(() => {
    dispatch(fetchAllFlatMenu(user?.restaurant_id));
  }, [user?.restaurant_id]);

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/course");
      return;
    }
    form.reset({
      type: "update",
      item_no: item?.item_no || "",
      name: item?.name || "",
      description: item?.description || "",
      course_items: item?.course_items || [],
      in_price: item?.in_price ?? null,
      out_price: item?.out_price ?? null,
      vat: item?.vat ?? 0,
      status: item?.status || "active",
      file_manager_id: item?.file_manager_id || undefined,
    });

    if (item?.file_manager_id && item?.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.name,
        },
      ]);
    }
    dispatch(setItemVariants(item?.course_items || []));
  }, [type, item?.id, form]);

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Course",
      user,
      createAction: createNewCourse,
      updateAction: updateExistingCourse,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/course",
    });

  if (getLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${type == "update" ? "Update Course" : "Add New Course"}`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        { name: "Courses", path: "/backoffice/menu/course" },
        {
          name: `${type == "update" ? "Update Course" : "Add New Course"}`,
          path: `/backoffice/menu/course/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {/* {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )} */}
        <Form {...form}>
          <form>
            {step === 0 && <Step1 course_id={course_id} form={form} />}
            {step === 1 && (
              <Step2
                    item_id={entityId || item?.id}
                course_id={course_id}
                dayTimes={dayTimes}
                setDayTimes={setDayTimes}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/course"
              onBack={prevStep}
              onNext={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form); // Step 1 & Step 2 → draft
              }}
              onSubmit={async () => {
                const data = form.getValues();
                await nextStep(data?.status, data, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateCourse;

export const createCourseschema = z.object({
  item_no: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  course_items: z.array(z.object({}).catchall(z.any())).optional(),
  in_price: z.preprocess(
    (val) =>
      val === null || val === undefined || val === "" ? undefined : Number(val),
    z
      .number({ invalid_type_error: "In pirce must be a number" })
      .nullable()
      .optional()
  ),
  out_price: z.preprocess(
    (val) =>
      val === null || val === undefined || val === "" ? undefined : Number(val),
    z
      .number({ invalid_type_error: "Out price must be a number" })
      .nullable()
      .optional()
  ),
  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT percentage must be a number",
      })
    )
    .refine((val) => val >= 0, {
      message: "VAT percentage cannot be negative",
    })
    .refine((val) => val <= 100, {
      message: "VAT percentage cannot exceed 100",
    })
    .optional(),
  file_manager_id: z
    .number({
      required_error: "Please upload an image",
      invalid_type_error: "File ID must be a number",
    })
    .int("File ID must be an integer")
    .optional(),
  status: z.enum(["active", "inactive", "draft"]).default("active"),
});
